import React from "react";
import { isEmpty } from "lodash";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Table from "examples/Tables/Table";
import GoalStatusChip from "./GoalStatusChip";
import GoalActionButton from "./GoalActionButton";
import Author from "layouts/dashboard/components/TableAuthor";
import { ENGINEERS_TABLE_COLUMNS } from "../goals.constants";

function GoalsEngineersTable({ engineers, isLoading, onViewDetails }) {
  const rows = engineers.map((engineer) => {
    const currentGoal = engineer.current_goal;
    const previousGoal = engineer.previous_goal;

    return {
      rowOnClick: () => onViewDetails(engineer),
      engineer: (
        <Author
          image={engineer.image_url}
          name={engineer.display_name || engineer.name}
          jobTitle={engineer.job_title}
        />
      ),
      current_goal: currentGoal ? (
        <SuiBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <SuiTypography variant="button" fontWeight="medium">
            {currentGoal.template_label || "Custom period"}
          </SuiTypography>
          <GoalStatusChip status={currentGoal.status} />
        </SuiBox>
      ) : (
        <GoalStatusChip />
      ),
      previous_score: previousGoal ? (
        <SuiBox>
          <SuiTypography variant="button" fontWeight="medium" display="block">
            {previousGoal.actual_percentage ?? "—"} / {previousGoal.total_percentage}%
          </SuiTypography>
          {previousGoal.template_label && (
            <SuiTypography variant="caption" color="secondary">
              ({previousGoal.template_label})
            </SuiTypography>
          )}
        </SuiBox>
      ) : (
        <SuiTypography variant="caption" color="secondary">
          No prior period
        </SuiTypography>
      ),
      last_set_by: (
        <SuiTypography variant="caption">
          {currentGoal?.set_by || previousGoal?.set_by || "—"}
        </SuiTypography>
      ),
      last_reviewed_by: (
        <SuiTypography variant="caption">
          {currentGoal?.reviewed_by || previousGoal?.reviewed_by || "—"}
        </SuiTypography>
      ),
      actions: (
        <SuiBox display="flex" gap={1} alignItems="center" justifyContent="center">
          {!currentGoal && (
            <GoalActionButton
              icon="flag"
              tooltip="Set goals"
              color="error"
              onClick={() => onViewDetails(engineer, { startSetup: true })}
            />
          )}
          {currentGoal?.status === "draft" && (
            <GoalActionButton
              icon="edit"
              tooltip="Edit setup"
              color="warning"
              onClick={() => onViewDetails(engineer, { startSetup: true })}
            />
          )}
          {currentGoal && currentGoal.status !== "draft" && (
            <GoalActionButton
              icon="fact_check"
              tooltip="Review goals"
              color="success"
              onClick={() => onViewDetails(engineer)}
            />
          )}
          <GoalActionButton
            icon="visibility"
            tooltip="View details"
            color="secondary"
            onClick={() => onViewDetails(engineer)}
          />
        </SuiBox>
      ),
    };
  });

  return (
    <Card>
      <SuiBox p={3}>
        {isLoading ? (
          <SuiBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress color="dark" size={30} />
          </SuiBox>
        ) : !isEmpty(rows) ? (
          <Table columns={ENGINEERS_TABLE_COLUMNS} rows={rows} />
        ) : (
          <SuiBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
              flag
            </Icon>
            <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
              No engineers found.
            </SuiTypography>
          </SuiBox>
        )}
      </SuiBox>
    </Card>
  );
}

export default GoalsEngineersTable;
